import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { type MoveId, inferMove, isMoveId } from '@goaltree/backend/convex/lib/exerciseMoves'
import { formatAmount } from '@goaltree/backend/convex/lib/habitMeasure'
import {
  type ItemKind,
  type ItemLog,
  type LastTime,
  type PlanItem,
  restSeconds,
} from '@goaltree/backend/convex/lib/sessionPlan'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import type { TFunction } from 'i18next'
import { Check, Dumbbell, Route, Square, Timer } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { TimerRing, clock, useNow } from '@/components/number-habit'
import { haptic } from '@/lib/haptics'
import { playBellSound, playSoftChime } from '@/lib/sounds'

// Running today's session: set ticks, the hold countdown (each side, played
// flows) and finishing. Shared by the sheet's checklist and the fullscreen view.

export type PlanView = NonNullable<FunctionReturnType<typeof api.sessionPlans.getPlan>>
export type SessionDoc = NonNullable<PlanView['session']>

const KIND_ICON = { sets: Dumbbell, timed: Timer, distance: Route } as const

export function KindIcon({ kind, className }: { kind: ItemKind; className?: string }) {
  const Icon = KIND_ICON[kind]
  return (
    <Icon className={cn('size-4 shrink-0 text-muted-foreground', className)} strokeWidth={2.2} />
  )
}

export const num = (n: number | undefined, lang: string) => formatAmount(n ?? 0, lang)

export function duration(t: TFunction, s: number, lang: string) {
  return s >= 60 && s % 60 === 0
    ? t('sessionPlan.min', { n: num(s / 60, lang) })
    : t('sessionPlan.sec', { n: num(s, lang) })
}

type Target = Pick<PlanItem, 'kind' | 'sets' | 'reps' | 'weight' | 'seconds' | 'distance' | 'unit'>

export function targetLine(t: TFunction, i: Target, lang: string) {
  const sets = i.sets > 1 ? `${i.sets} × ` : ''
  if (i.kind === 'sets')
    return `${i.sets} × ${num(i.reps, lang)}${i.weight ? ` · ${num(i.weight, lang)} ${i.unit ?? 'kg'}` : ''}`
  if (i.kind === 'timed') return `${sets}${duration(t, i.seconds ?? 0, lang)}`
  return `${sets}${num(i.distance, lang)} ${i.unit ?? 'km'}`
}

export function lastLine(t: TFunction, l: LastTime, lang: string) {
  const sets = l.sets > 1 ? `${l.sets} × ` : ''
  const what =
    l.kind === 'sets'
      ? `${l.sets} × ${num(l.reps, lang)}${l.weight ? ` @ ${num(l.weight, lang)} ${l.unit ?? 'kg'}` : ''}`
      : l.kind === 'timed'
        ? `${sets}${duration(t, l.seconds ?? 0, lang)}`
        : `${sets}${num(l.distance, lang)} ${l.unit ?? 'km'}`
  return t('sessionPlan.lastTime', { what })
}

export const logTarget = (l: ItemLog): Target => ({
  kind: l.kind,
  sets: l.sets.length,
  unit: l.unit,
  ...l.target,
})

export type SetPatch = {
  done?: boolean
  reps?: number
  weight?: number
  seconds?: number
  distance?: number
}

// 'next' is the pause before the following pose in a played flow.
export type Rest = { endsAt: number; total: number; paused?: number }

export type HoldTimer = {
  itemId: string
  set: number
  phase: 'one' | 'left' | 'switch' | 'right' | 'next'
  endsAt: number
  // ms left while paused.
  paused?: number
}

export const SWITCH_MS = 5000
const FLOW_GAP_MS = 4000

export const phaseMs = (h: HoldTimer, l?: ItemLog) =>
  h.phase === 'switch'
    ? SWITCH_MS
    : h.phase === 'next'
      ? FLOW_GAP_MS
      : (l?.target.seconds ?? 60) * 1000

export const timerLeft = (h: HoldTimer, now: number) => h.paused ?? Math.max(0, h.endsAt - now)

export function useSessionRunner({
  habitId,
  view,
  session,
  doneToday,
  onComplete,
  quiet,
}: {
  habitId: Id<'nodes'>
  view: PlanView | undefined
  session: SessionDoc | null | undefined
  doneToday: boolean
  onComplete: (id: Id<'nodes'>) => void
  // The fullscreen view celebrates; skip the toast.
  quiet: boolean
}) {
  const { t } = useTranslation()
  const update = useMutation(api.sessionPlans.updateSet).withOptimisticUpdate((store, args) => {
    const cur = store.getQuery(api.sessionPlans.getPlan, { habitId })
    if (!cur?.session) return
    const { sessionId: _s, itemId, set, ...patch } = args
    const clean = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined))
    store.setQuery(
      api.sessionPlans.getPlan,
      { habitId },
      {
        ...cur,
        session: {
          ...cur.session,
          items: cur.session.items.map((l) =>
            l.itemId !== itemId
              ? l
              : { ...l, sets: l.sets.map((x, i) => (i === set ? { ...x, ...clean } : x)) },
          ),
        },
      },
    )
  })
  const finishM = useMutation(api.sessionPlans.finishSession)
  const finishing = useRef(false)
  // Fullscreen finishes tick the habit when the view closes, so completion
  // dialogs (badges, level-ups) don't open underneath it.
  const pending = useRef(false)
  const completeRef = useRef(() => onComplete(habitId))
  completeRef.current = () => onComplete(habitId)
  const [timer, setTimer] = useState<HoldTimer | null>(null)
  const [flow, setFlow] = useState(false)
  const [finished, setFinished] = useState<{ prs: string[] } | null>(null)
  const [rest, setRest] = useState<Rest | null>(null)
  const now = useNow((!!timer && timer.paused === undefined) || !!rest, 250)

  const active = session?.status === 'active' ? session : null
  const items = active?.items ?? []
  const planSession = view?.plan?.sessions.find((s) => s.id === active?.sessionId)
  const planItem = (itemId: string) => planSession?.items.find((i) => i.id === itemId)
  const moveOf = (l: ItemLog): MoveId => {
    const stored = l.move ?? planItem(l.itemId)?.move
    return isMoveId(stored) ? stored : inferMove(l.name, l.kind)
  }
  const remaining = items.filter((l) => l.sets.some((s) => !s.done)).length
  const untouched = items.every((l) => l.sets.every((s) => !s.done))
  const allTimed = items.length > 0 && items.every((l) => l.kind === 'timed')

  const startRest = (l: ItemLog) => {
    const secs = restSeconds(l)
    setRest(secs > 0 ? { endsAt: Date.now() + secs * 1000, total: secs * 1000 } : null)
  }
  const skipRest = () => setRest(null)
  const addRest = (ms = 15_000) =>
    setRest((r) => (r ? { ...r, endsAt: r.endsAt + ms, total: r.total + ms } : r))

  const finish = () => {
    if (finishing.current || !active) return
    finishing.current = true
    setTimer(null)
    setRest(null)
    setFlow(false)
    finishM({ sessionId: active._id })
      .then((r) => {
        setFinished({ prs: r.prs })
        if (r.prs.length && !quiet) toast.success(t('sessionPlan.prs', { list: r.prs.join(', ') }))
        if (doneToday) return
        if (quiet) pending.current = true
        else onComplete(habitId)
      })
      .catch(() => toast.error(t('sessionPlan.error')))
      .finally(() => {
        finishing.current = false
      })
  }

  const patchSet = (l: ItemLog, set: number, patch: SetPatch) => {
    if (!active) return
    void update({ sessionId: active._id, itemId: l.itemId, set, ...patch }).catch(() =>
      toast.error(t('sessionPlan.error')),
    )
    if (patch.done) {
      haptic('light')
      const allDone = items.every((x) =>
        x.sets.every((s, i) => s.done || (x.itemId === l.itemId && i === set)),
      )
      if (allDone) finish()
      else if (!flow) startRest(l)
    } else if (patch.done === false) setRest(null)
  }

  // The first undone set after (itemId, set), in order; timed only during a flow.
  const nextUndone = (after?: { itemId: string; set: number }, timedOnly = false) => {
    const order = items.flatMap((l) =>
      timedOnly && l.kind !== 'timed' ? [] : l.sets.map((s, i) => ({ l, set: i, done: s.done })),
    )
    const from = after
      ? order.findIndex((x) => x.l.itemId === after.itemId && x.set === after.set) + 1
      : 0
    return order.slice(from).find((x) => !x.done) ?? null
  }

  const startTimer = (itemId: string, set: number) => {
    const l = items.find((x) => x.itemId === itemId)
    if (!l) return
    haptic('light')
    setRest(null)
    setTimer({
      itemId,
      set,
      phase: l.eachSide ? 'left' : 'one',
      endsAt: Date.now() + (l.target.seconds ?? 60) * 1000,
    })
  }

  const stopTimer = () => {
    setTimer(null)
    setFlow(false)
  }

  const togglePause = () =>
    setTimer((h) =>
      !h
        ? h
        : h.paused === undefined
          ? { ...h, paused: Math.max(0, h.endsAt - Date.now()) }
          : { ...h, endsAt: Date.now() + h.paused, paused: undefined },
    )

  const queueNext = (after: { itemId: string; set: number }) => {
    const next = nextUndone(after, true)
    if (!next) {
      setTimer(null)
      setFlow(false)
      return
    }
    setTimer({
      itemId: next.l.itemId,
      set: next.set,
      phase: 'next',
      endsAt: Date.now() + FLOW_GAP_MS,
    })
  }

  const startFlow = () => {
    const first = nextUndone(undefined, true)
    if (!first) return
    setFlow(true)
    startTimer(first.l.itemId, first.set)
  }

  const skip = () => {
    if (!timer) return
    if (timer.phase === 'next') startTimer(timer.itemId, timer.set)
    else if (flow) queueNext(timer)
    else setTimer(null)
  }

  const step = useRef<() => void>(() => {})
  step.current = () => {
    if (rest && now >= rest.endsAt) {
      setRest(null)
      playBellSound()
    }
    if (!timer || timer.paused !== undefined || now < timer.endsAt) return
    const l = items.find((x) => x.itemId === timer.itemId)
    if (!l) return setTimer(null)
    const secs = (l.target.seconds ?? 60) * 1000
    if (timer.phase === 'left') {
      playSoftChime()
      return setTimer({ ...timer, phase: 'switch', endsAt: Date.now() + SWITCH_MS })
    }
    if (timer.phase === 'switch') {
      haptic('medium')
      return setTimer({ ...timer, phase: 'right', endsAt: Date.now() + secs })
    }
    if (timer.phase === 'next') return startTimer(timer.itemId, timer.set)
    const last = !nextUndone(timer, true) || !flow
    if (flow && !last) playSoftChime()
    else playBellSound()
    patchSet(l, timer.set, { done: true, seconds: l.target.seconds })
    if (flow) queueNext(timer)
    else setTimer(null)
  }
  useEffect(() => step.current(), [now])

  useEffect(() => {
    if (!active) {
      setTimer(null)
      setFlow(false)
      setRest(null)
    }
  }, [active])

  const reset = useCallback(() => {
    setFinished(null)
    if (!pending.current) return
    pending.current = false
    completeRef.current()
  }, [])
  useEffect(() => {
    const flush = () => {
      if (!pending.current) return
      pending.current = false
      completeRef.current()
    }
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [])

  return {
    session: active,
    items,
    timer,
    rest,
    now,
    flow,
    finished,
    remaining,
    untouched,
    allTimed,
    planItem,
    moveOf,
    patchSet,
    finish,
    startTimer,
    stopTimer,
    togglePause,
    startFlow,
    skip,
    skipRest,
    addRest,
    lastOf: (itemId: string) => view?.lastByItem[itemId],
    nextUndone,
    reset,
  }
}

export type Runner = ReturnType<typeof useSessionRunner>

export function NumField({
  value,
  onCommit,
  label,
  step = 1,
  className,
}: {
  value: number | undefined
  onCommit: (n: number) => void
  label: string
  step?: number
  className?: string
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value))
  useEffect(() => setText(value === undefined ? '' : String(value)), [value])
  return (
    <input
      type="number"
      inputMode="decimal"
      min={0}
      step={step}
      aria-label={label}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const n = Number(text)
        if (text !== '' && Number.isFinite(n) && n >= 0 && n !== value) onCommit(n)
        else setText(value === undefined ? '' : String(value))
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      className={cn(
        'h-7 w-14 min-w-0 rounded-md border border-border/70 bg-background px-1.5 text-center font-mono text-xs tabular-nums text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        className,
      )}
    />
  )
}

export function phaseLabel(t: TFunction, h: HoldTimer, items: ItemLog[]) {
  if (h.phase === 'left') return t('sessionPlan.leftSide')
  if (h.phase === 'right') return t('sessionPlan.rightSide')
  if (h.phase === 'switch') return t('sessionPlan.switchSides')
  if (h.phase === 'next')
    return t('sessionPlan.upNext', { name: items.find((l) => l.itemId === h.itemId)?.name ?? '' })
  return null
}

const ghost =
  'inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
const outline =
  'inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-full border border-border/70 px-3 py-1 text-xs font-semibold text-foreground transition-colors hover:bg-muted'

// One row per set: tick, the reps / weight / distance actually done, and the
// countdown for timed sets. `large` is the fullscreen focus view.
export function SetRows({
  runner,
  log: l,
  large,
}: {
  runner: Runner
  log: ItemLog
  large?: boolean
}) {
  const { t, i18n } = useTranslation()
  const { timer, now } = runner
  const field = large ? 'h-10 w-16 text-base' : undefined
  return (
    <div className={cn('flex flex-col', large ? 'gap-2.5' : 'gap-1')}>
      {l.sets.map((s, i) => {
        const running = timer?.itemId === l.itemId && timer.set === i && timer.phase !== 'next'
        const label = running ? phaseLabel(t, timer, runner.items) : null
        return (
          <div
            key={i}
            className={cn(
              'flex flex-wrap items-center gap-1.5',
              large && 'gap-2 rounded-xl border border-border/60 px-3 py-2',
              large && s.done && 'border-[var(--habit-accent,var(--primary))]/40 bg-primary/5',
            )}
          >
            <button
              type="button"
              role="checkbox"
              aria-checked={s.done}
              aria-label={t('sessionPlan.setN', { n: i + 1 })}
              onClick={() => runner.patchSet(l, i, { done: !s.done })}
              className={cn(
                'flex shrink-0 cursor-pointer items-center justify-center rounded-full border-2 transition-colors',
                large ? 'size-10' : 'size-6',
                s.done
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted-foreground/40 hover:border-primary',
              )}
            >
              {s.done && <Check className={large ? 'size-5' : 'size-3.5'} strokeWidth={3} />}
            </button>
            <span
              className={cn('text-muted-foreground', large ? 'w-12 text-sm' : 'w-10 text-[11px]')}
            >
              {t('sessionPlan.setN', { n: i + 1 })}
            </span>
            {l.kind === 'sets' && (
              <>
                <NumField
                  value={s.reps}
                  label={t('sessionPlan.reps')}
                  className={field}
                  onCommit={(reps) => runner.patchSet(l, i, { reps })}
                />
                <span className={cn('text-muted-foreground', large ? 'text-sm' : 'text-[11px]')}>
                  {t('sessionPlan.reps')}
                </span>
                {l.target.weight !== undefined && (
                  <>
                    <NumField
                      value={s.weight}
                      step={0.5}
                      label={t('sessionPlan.weight')}
                      className={field}
                      onCommit={(weight) => runner.patchSet(l, i, { weight })}
                    />
                    <span
                      className={cn('text-muted-foreground', large ? 'text-sm' : 'text-[11px]')}
                    >
                      {l.unit ?? 'kg'}
                    </span>
                  </>
                )}
              </>
            )}
            {l.kind === 'distance' && (
              <>
                <NumField
                  value={s.distance}
                  step={0.1}
                  label={t('sessionPlan.distance')}
                  className={field}
                  onCommit={(distance) => runner.patchSet(l, i, { distance })}
                />
                <span className={cn('text-muted-foreground', large ? 'text-sm' : 'text-[11px]')}>
                  {l.unit ?? 'km'}
                </span>
              </>
            )}
            {l.kind === 'timed' &&
              (running && large ? (
                <span className="flex items-center gap-1.5">
                  {label && <span className="text-sm text-muted-foreground">{label}</span>}
                  <button
                    type="button"
                    className={ghost}
                    aria-label={t('sessionPlan.timerStop')}
                    onClick={runner.stopTimer}
                  >
                    <Square className="size-3.5" />
                  </button>
                </span>
              ) : running ? (
                <span className="flex items-center gap-1.5">
                  <TimerRing ratio={1 - timerLeft(timer, now) / phaseMs(timer, l)} />
                  <span className="flex flex-col leading-tight">
                    <span className="font-mono text-sm font-semibold tabular-nums" role="timer">
                      {clock(timerLeft(timer, now))}
                    </span>
                    {label && <span className="text-[11px] text-muted-foreground">{label}</span>}
                  </span>
                  <button
                    type="button"
                    className={ghost}
                    aria-label={t('sessionPlan.timerStop')}
                    onClick={runner.stopTimer}
                  >
                    <Square className="size-3" />
                  </button>
                </span>
              ) : (
                <>
                  <span
                    className={cn('font-mono text-muted-foreground', large ? 'text-sm' : 'text-xs')}
                  >
                    {duration(t, s.seconds ?? l.target.seconds ?? 0, i18n.language)}
                    {l.eachSide && ` · ${t('sessionPlan.eachSide')}`}
                  </span>
                  {!s.done && (
                    <button
                      type="button"
                      className={cn(outline, large && 'px-4 py-2 text-sm')}
                      onClick={() => runner.startTimer(l.itemId, i)}
                    >
                      <Timer className="size-3" />
                      {t('sessionPlan.timerStart')}
                    </button>
                  )}
                </>
              ))}
          </div>
        )
      })}
    </div>
  )
}
